import { useEffect } from 'react';
import { Stack, Title, Card, TextInput, Select, Button, Group, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { api } from '../../api/client.ts';
import { useAuthStore } from '../../auth/store.ts';

/**
 * Account settings — first/last/email/langKey. Maps to POST /api/account.
 * The server has unusual quirks on `/api/register` and password-reset
 * (admin-only on this deployment) so we don't expose those screens to
 * regular users; only the in-place profile update is here.
 */
const LANG_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
];

interface AccountForm {
  firstName: string;
  lastName: string;
  email: string;
  imageUrl: string;
  langKey: string;
}

export function SettingsPage() {
  const user = useAuthStore(s => s.user);
  const setUser = useAuthStore(s => s.setUser);
  const qc = useQueryClient();

  const form = useForm<AccountForm>({
    initialValues: { firstName: '', lastName: '', email: '', imageUrl: '', langKey: 'en' },
    validate: {
      email: v => (/\S+@\S+\.\S+/.test(v) ? null : 'Valid email required'),
    },
  });

  useEffect(() => {
    if (!user) return;
    form.setValues({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
      imageUrl: user.imageUrl ?? '',
      langKey: user.langKey ?? 'en',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.login]);

  async function submit(values: AccountForm) {
    try {
      const res = await api.post('/api/account', values);
      if (res.status !== 200) {
        notifications.show({ message: `Save failed: ${res.status}`, color: 'red' });
        return;
      }
      const me = await api.get('/api/account');
      if (me.status === 200) setUser(me.data);
      qc.invalidateQueries({ queryKey: ['account'] });
      notifications.show({ message: 'Settings saved', color: 'green' });
    } catch (err) {
      notifications.show({
        message: 'Save failed' + (err instanceof Error ? `: ${err.message}` : ''),
        color: 'red',
      });
    }
  }

  if (!user) return null;

  return (
    <Stack gap="md">
      <Title order={2}>Settings</Title>
      <Card withBorder padding="md" radius="md" maw={640}>
        <Stack gap="sm">
          <Group>
            <Text size="sm" c="dimmed">Signed in as</Text>
            <Text size="sm" fw={500}>{user.login}</Text>
          </Group>
          <form onSubmit={form.onSubmit(submit)}>
            <Stack gap="sm">
              <Group grow>
                <TextInput label="First name" {...form.getInputProps('firstName')} />
                <TextInput label="Last name" {...form.getInputProps('lastName')} />
              </Group>
              <TextInput label="Email" type="email" required {...form.getInputProps('email')} />
              <Select
                label="Language"
                data={LANG_OPTIONS}
                value={form.values.langKey}
                onChange={v => form.setFieldValue('langKey', v ?? 'en')}
                allowDeselect={false}
              />
              <Group justify="flex-end" mt="sm">
                <Button type="submit" leftSection={<IconDeviceFloppy size={16} />}>
                  Save
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Card>
    </Stack>
  );
}
